import React, { useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';

function idxToRC(idx, cols) {
  return [Math.floor(idx / cols), idx % cols];
}

function rcToIdx(r, c, cols) {
  return r * cols + c;
}

export default function IdiomsGridEditor({ base, gridSize: gridSizeProp = [7, 7], idioms: idiomsProp = [] }) {
  const { setFieldValue, values } = useFormikContext();

  // helper to read nested form values by path like 'questionSets[0].questions.1.idioms'
  const getAt = (obj, path) => {
    if (!path) return undefined;
    const p = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    let cur = obj;
    for (let i = 0; i < p.length; i += 1) {
      const k = p[i];
      if (k === '') continue;
      if (cur == null) return undefined;
      cur = cur[k];
    }
    return cur;
  };

  const idiomsFromForm = getAt(values, `${base}.idioms`);
  const gridSizeFromForm = getAt(values, `${base}.gridSize`);

  const idioms = Array.isArray(idiomsFromForm) ? idiomsFromForm : idiomsProp || [];
  const gridSize = Array.isArray(gridSizeFromForm) ? gridSizeFromForm : (Array.isArray(gridSizeProp) ? gridSizeProp : [7, 7]);
  const rows = Number(gridSize[0] || 7);
  const cols = Number(gridSize[1] || 7);

  // initialize cells and blank positions from idioms patterns
  const initialState = useMemo(() => {
    const cells = new Array(rows * cols).fill('');
    const blanks = new Set();
    // defensive: coerce start/end to numbers and log a small sample for debugging
    try {
      // eslint-disable-next-line no-console
      console.log && console.log('IdiomsGridEditor init', { gridSize: [rows, cols], idioms: (idioms || []).slice(0, 3) });
    } catch (e) {}
    (idioms || []).forEach((idm) => {
      const startRaw = Array.isArray(idm.start) ? idm.start : [0, 0];
      const endRaw = Array.isArray(idm.end) ? idm.end : [0, 0];
      const start = startRaw.map((v) => Number(v));
      const end = endRaw.map((v) => Number(v));
      const dir = idm.direction || (start[0] === end[0] ? 'horizontal' : 'vertical');
      let pattern = Array.isArray(idm.pattern) ? idm.pattern : [];
      const fullAnswers = Array.isArray(idm.answers) && idm.answers.length > 0 ? idm.answers[0] : null;
      if ((pattern == null || pattern.length === 0) && fullAnswers && typeof fullAnswers === 'string') {
        pattern = fullAnswers.split('');
      }
      if (dir === 'horizontal') {
        const r = start[0];
        const sc = Math.min(start[1], end[1]);
        const spanLen = Math.abs((end[1] || 0) - (start[1] || 0)) + 1;
        const len = Math.max(spanLen, pattern.length);
        for (let i = 0; i < len; i += 1) {
          const c = sc + i;
          if (r >= 0 && r < rows && c >= 0 && c < cols) {
            const idx = rcToIdx(r, c, cols);
            const ch = (typeof pattern[i] !== 'undefined') ? pattern[i] : '';
            cells[idx] = (ch || '');
            if (!ch) blanks.add(idx);
          }
        }
      } else {
        const c = start[1];
        const sr = Math.min(start[0], end[0]);
        const spanLen = Math.abs((end[0] || 0) - (start[0] || 0)) + 1;
        const len = Math.max(spanLen, pattern.length);
        for (let i = 0; i < len; i += 1) {
          const r = sr + i;
          if (r >= 0 && r < rows && c >= 0 && c < cols) {
            const idx = rcToIdx(r, c, cols);
            const ch = (typeof pattern[i] !== 'undefined') ? pattern[i] : '';
            cells[idx] = (ch || '');
            if (!ch) blanks.add(idx);
          }
        }
      }
    });
    return { cells, blanks };
  }, [rows, cols, JSON.stringify(idioms || [])]);

  const [cells, setCells] = useState(initialState.cells);
  const [blankSet, setBlankSet] = useState(initialState.blanks);
  useEffect(() => {
    const initialCellsRef = initialState.cells;
    setCells((prev) => {
      if (prev.length === initialCellsRef.length) {
        let same = true;
        for (let i = 0; i < prev.length; i += 1) {
          if (prev[i] !== initialCellsRef[i]) { same = false; break; }
        }
        if (same) return prev;
      }
      return initialCellsRef;
    });
    setBlankSet(new Set(initialState.blanks));
  }, [initialState]);

  const [selectedIdx, setSelectedIdx] = useState(null);
  const [startIdx, setStartIdx] = useState(null);
  const [endIdx, setEndIdx] = useState(null);

  useEffect(() => {
    // if grid size changes, ensure cells length matches
    if (cells.length !== rows * cols) setCells(new Array(rows * cols).fill(''));
  }, [rows, cols]);

  const handleCellChange = (idx, val) => {
    const next = cells.slice();
    next[idx] = val.slice(0, 1); // single character
    setCells(next);
  };

  const createIdiomFromSelection = (start = startIdx, end = endIdx) => {
    if (start == null || end == null) return;
    const [sr, sc] = idxToRC(start, cols);
    const [er, ec] = idxToRC(end, cols);
    let direction = null;
    const pattern = [];
    if (sr === er) {
      direction = 'horizontal';
      const s = Math.min(sc, ec);
      const e = Math.max(sc, ec);
      for (let c = s; c <= e; c += 1) pattern.push(cells[rcToIdx(sr, c, cols)] || '');
      const idiom = {
        start: [sr, s], end: [er, e], direction, pattern,
      };
      setFieldValue(`${base}.idioms`, [...(idioms || []), idiom]);
    } else if (sc === ec) {
      direction = 'vertical';
      const s = Math.min(sr, er);
      const e = Math.max(sr, er);
      for (let r = s; r <= e; r += 1) pattern.push(cells[rcToIdx(r, sc, cols)] || '');
      const idiom = {
        start: [s, sc], end: [e, ec], direction, pattern,
      };
      setFieldValue(`${base}.idioms`, [...(idioms || []), idiom]);
    } else {
      // invalid non-straight selection
      // simply ignore for now
    }
  };

  const clearSelection = () => { setStartIdx(null); setEndIdx(null); setSelectedIdx(null); };

  const handleSetEndAndCreate = () => {
    if (selectedIdx == null) return;
    setEndIdx(selectedIdx);
    createIdiomFromSelection(startIdx, selectedIdx);
  };

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Grid editor ({rows} × {cols}) — click a cell and type one character</div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 36px)`, gap: 4 }}>
          {cells.map((ch, idx) => {
            const [r, c] = idxToRC(idx, cols);
            const isSelected = idx === selectedIdx;
            const isStart = idx === startIdx;
            const isEnd = idx === endIdx;
            const isBlank = blankSet && blankSet.has && blankSet.has(idx);
            return (
              <input
                key={`${r}-${c}`}
                value={ch}
                placeholder={isBlank ? '□' : ''}
                onChange={(e) => handleCellChange(idx, e.target.value)}
                onFocus={() => setSelectedIdx(idx)}
                onClick={() => setSelectedIdx(idx)}
                style={{
                  width: 36, height: 36, textAlign: 'center', fontSize: 16, border: isStart ? '2px solid #007bff' : (isEnd ? '2px solid #28a745' : (isSelected ? '2px solid #999' : '1px solid #ddd')), borderRadius: 4, background: isBlank ? '#fff9f0' : '#fff',
                }}
              />
            );
          })}
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8,
      }}>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { if (selectedIdx != null) setStartIdx(selectedIdx); }}>Set Start</button>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleSetEndAndCreate}>Set End</button>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={() => {
            setCells(new Array(rows * cols).fill(''));
            setFieldValue(`${base}.idioms`, []);
            clearSelection();
          }}
        >
          Clear Grid
        </button>
      </div>
    </div>
  );
}
